import { BaseAssessRule } from '../assess-rules'
import { BaseProtectRule } from '../protect-rules/shared'
import { GroupedRule, ruleGroupI18n } from './rule-group'
import { allRulesType, allRulesTypeI18n } from './all-rules'
import { IntlFormatters } from 'react-intl'
import { ComboboxItem } from '../../legacy-design-system/combobox/shared'

export const getAssessOnlyRules = (
  commonRules: BaseProtectRule[],
  allAssessRules: BaseAssessRule[]
): BaseAssessRule[] => {
  return commonRules.length > 0
    ? allAssessRules.filter(
        (
          (set) => (a) =>
            !set.has(a.name)
        )(new Set(commonRules.map((b) => b.uuid)))
      )
    : allAssessRules
}

export const getProtectOnlyRules = (
  commonRules: BaseProtectRule[],
  allProtectRules: BaseProtectRule[]
): BaseProtectRule[] => {
  return commonRules.length > 0
    ? allProtectRules.filter(
        (
          (set) => (a) =>
            !set.has(a.uuid)
        )(new Set(commonRules.map((b) => b.uuid)))
      )
    : allProtectRules
}

export const getCommonRules = (
  assessRules: BaseAssessRule[],
  protectRules: BaseProtectRule[]
): BaseProtectRule[] => {
  return protectRules.filter(
    (
      (set) => (protectRule) =>
        set.has(protectRule.uuid)
    )(new Set(assessRules.map((assessRule) => assessRule.name)))
  )
}

export const isAllRulesInferred = (selectedRules: GroupedRule[]) => {
  return selectedRules.length === 0
}

export const isAllProtectRulesFound = (selectedRules: GroupedRule[]) => {
  return !!selectedRules.find(
    (s) => s.isAllOption && s.value === allRulesType.ALL_PROTECT
  )
}

export const isAllAssessRulesFound = (selectedRules: GroupedRule[]) => {
  return !!selectedRules.find(
    (s) => s.isAllOption && s.value === allRulesType.ALL_ASSESS
  )
}

export const handleNewAllRuleOption = (selectedRules: GroupedRule[]) => {
  const newRule = selectedRules[selectedRules.length - 1]
  switch (newRule.value) {
    case 'ALL':
      return []
    case 'ALL_PROTECT':
      const hasAllAssess = selectedRules.some(
        (rule) => rule.value === allRulesType.ALL_ASSESS
      )

      return hasAllAssess
        ? selectedRules.filter(
            (rule) =>
              rule.value === allRulesType.ALL_PROTECT ||
              rule.value === allRulesType.ALL_ASSESS
          )
        : selectedRules.filter(
            (rule) =>
              rule.value === allRulesType.ALL_PROTECT ||
              rule.group !== 'PROTECT'
          )
    case 'ALL_ASSESS':
      const hasAllProtect = selectedRules.some(
        (rule) => rule.value === allRulesType.ALL_PROTECT
      )
      return hasAllProtect
        ? selectedRules.filter(
            (rule) =>
              rule.value === allRulesType.ALL_PROTECT ||
              rule.value === allRulesType.ALL_ASSESS
          )
        : selectedRules.filter(
            (rule) =>
              rule.value === allRulesType.ALL_ASSESS || rule.group !== 'ASSESS'
          )
    default:
      return selectedRules
  }
}

export const handleNewRule = (selectedRules: GroupedRule[]) => {
  const newRule = selectedRules[selectedRules.length - 1]
  switch (newRule.group) {
    case 'ASSESS':
      return selectedRules.filter((rule) => {
        return (
          rule.value !== allRulesType.ALL_ASSESS &&
          rule.value !== allRulesType.ALL
        )
      })
    case 'PROTECT':
      return selectedRules.filter((rule) => {
        return (
          rule.value !== allRulesType.ALL_PROTECT &&
          rule.value !== allRulesType.ALL
        )
      })
    case 'BOTH':
      const hasAllAssessAndAllProtect =
        selectedRules.filter(
          (rule) =>
            rule.value === allRulesType.ALL_ASSESS ||
            rule.value === allRulesType.ALL_PROTECT
        ).length === 2
      const hasAllRules = selectedRules.some(
        (rule) => rule.value === allRulesType.ALL
      )
      return hasAllAssessAndAllProtect || hasAllRules
        ? [newRule]
        : selectedRules
    default:
      return selectedRules
  }
}

export const getGroupedRuleItems = (
  commonRules: BaseProtectRule[],
  assessOnlyRules: BaseAssessRule[],
  protectOnlyRules: BaseProtectRule[],
  selectedRules: GroupedRule[],
  formatMessage: IntlFormatters['formatMessage']
): Array<ComboboxItem<GroupedRule>> => {
  const allRules = isAllRulesInferred(selectedRules)
  const allProtectRules = isAllProtectRulesFound(selectedRules)
  const allAssessRules = isAllAssessRulesFound(selectedRules)

  const ruleItems: Array<ComboboxItem<GroupedRule>> = []

  if (!allRules) {
    ruleItems.push({
      name: undefined,
      items: [
        {
          value: allRulesType.ALL,
          name: formatMessage({ id: allRulesTypeI18n.ALL }),
          isAllOption: true
        }
      ]
    })
  }

  if (protectOnlyRules.length > 0) {
    const protectGroupedRules: GroupedRule[] = protectOnlyRules.map(
      ({ displayName, uuid }) => ({
        value: uuid,
        name: displayName,
        group: 'PROTECT',
        isAllOption: false
      })
    )

    ruleItems.push({
      name: formatMessage({ id: ruleGroupI18n.PROTECT }),
      items: !allProtectRules
        ? [
            {
              value: allRulesType.ALL_PROTECT,
              name: formatMessage({ id: allRulesTypeI18n.ALL_PROTECT }),
              group: 'PROTECT',
              isAllOption: true
            },
            ...protectGroupedRules
          ]
        : protectGroupedRules
    })
  }

  if (assessOnlyRules.length > 0) {
    const assessGroupedRules: GroupedRule[] = assessOnlyRules.map(
      ({ name, title }) => ({
        value: name,
        name: title,
        group: 'ASSESS',
        isAllOption: false
      })
    )

    ruleItems.push({
      name: formatMessage({ id: ruleGroupI18n.ASSESS }),
      items: !allAssessRules
        ? [
            {
              value: allRulesType.ALL_ASSESS,
              name: formatMessage({ id: allRulesTypeI18n.ALL_ASSESS }),
              group: 'ASSESS',
              isAllOption: true
            },
            ...assessGroupedRules
          ]
        : assessGroupedRules
    })
  }

  if (commonRules.length > 0) {
    ruleItems.push({
      name: formatMessage({ id: ruleGroupI18n.BOTH }),
      items: commonRules.map(({ displayName, uuid }) => ({
        value: uuid,
        name: displayName,
        group: 'BOTH',
        isAllOption: false
      }))
    })
  }

  return ruleItems
}
